import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './SignalFreshnessMonitorTable.css';
import { buildScopedApiUrl, getApiHeaders } from './config';
import { useWorkspace } from './workspace/WorkspaceContext';

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const formatDuration = (seconds) => {
  if (seconds === undefined || seconds === null || seconds < 0) return '-';
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const restMinutes = minutes % 60;
  if (hours < 24) return restMinutes ? `${hours}h ${restMinutes}m` : `${hours}h`;
  const days = Math.floor(hours / 24);
  const restHours = hours % 24;
  return restHours ? `${days}d ${restHours}h` : `${days}d`;
};

const badgeClass = (status) => `signal-freshness-badge ${(status || '').toLowerCase()}`;

export default function SignalFreshnessMonitorTable() {
  const { workspace } = useWorkspace();
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState('');
  const [activeOnly, setActiveOnly] = useState(true);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const fetchRows = useCallback(() => {
    setLoading(true);
    setError('');
    setMessage('');

    fetch(buildScopedApiUrl('/api/bot/ui/freshness/monitors', workspace, {
      status,
      activeOnly,
    }), {
      headers: getApiHeaders(),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to fetch freshness monitor: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        const safeRows = Array.isArray(data) ? data : [];
        setRows(safeRows);
        setMessage(`Loaded ${safeRows.length} freshness monitor rows`);
      })
      .catch((fetchError) => {
        console.error(fetchError);
        setError(fetchError.message || 'Unable to fetch signal freshness monitor');
        setRows([]);
      })
      .finally(() => setLoading(false));
  }, [activeOnly, status, workspace]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const updateRow = (id, action, params = {}) => {
    setLoading(true);
    setError('');
    setMessage('');

    fetch(buildScopedApiUrl(`/api/bot/ui/freshness/${id}/${action}`, workspace, params), {
      method: 'POST',
      headers: getApiHeaders(),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`${action} failed: ${response.status}`);
        }
        return response.json();
      })
      .then(() => fetchRows())
      .catch((actionError) => {
        console.error(actionError);
        setError(actionError.message || `Unable to ${action} monitor row`);
      })
      .finally(() => setLoading(false));
  };

  const visibleRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((row) => [
      row.symbol,
      row.timeFrame,
      row.status,
      row.lastCandleCloseTime,
      row.notes,
    ].filter(Boolean).join(' ').toLowerCase().includes(term));
  }, [rows, search]);

  const summary = useMemo(() => {
    const counts = { total: rows.length, ACTIVE: 0, STALE: 0, MISSING: 0, INACTIVE: 0, MARKET_CLOSED: 0 };
    rows.forEach((row) => {
      if (counts[row.status] !== undefined) {
        counts[row.status] += 1;
      }
    });
    return counts;
  }, [rows]);

  return (
    <div className="signal-freshness-wrapper">
      <div className="signal-freshness-header">
        <div>
          <h2>Signal Freshness Monitor</h2>
          <p>Workspace: <strong>{workspace.exchange}</strong> / <strong>{workspace.marketType}</strong></p>
        </div>
        <div className="signal-freshness-toolbar">
          <button type="button" onClick={fetchRows} disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </button>
          <button type="button" onClick={() => { setStatus(''); setActiveOnly(true); setSearch(''); }}>
            Reset Filters
          </button>
        </div>
      </div>

      <div className="signal-freshness-summary">
        <div className="signal-freshness-summary-card"><span>Total</span><strong>{summary.total}</strong></div>
        <div className="signal-freshness-summary-card"><span>Active</span><strong>{summary.ACTIVE}</strong></div>
        <div className="signal-freshness-summary-card"><span>Stale</span><strong>{summary.STALE}</strong></div>
        <div className="signal-freshness-summary-card"><span>Missing</span><strong>{summary.MISSING}</strong></div>
        <div className="signal-freshness-summary-card"><span>Inactive</span><strong>{summary.INACTIVE}</strong></div>
        <div className="signal-freshness-summary-card"><span>Market closed</span><strong>{summary.MARKET_CLOSED}</strong></div>
      </div>

      <div className="signal-freshness-filters">
        <label className="signal-freshness-filter">
          Status
          <select value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="">ALL</option>
            <option value="ACTIVE">ACTIVE</option>
            <option value="STALE">STALE</option>
            <option value="MISSING">MISSING</option>
            <option value="INACTIVE">INACTIVE</option>
            <option value="MARKET_CLOSED">MARKET CLOSED</option>
          </select>
        </label>
        <label className="signal-freshness-filter">
          Search
          <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="symbol, timeframe, notes" />
        </label>
        <label className="signal-freshness-filter signal-freshness-filter--checkbox">
          <input type="checkbox" checked={activeOnly} onChange={(event) => setActiveOnly(event.target.checked)} />
          Active only
        </label>
      </div>

      {error && <div className="signal-freshness-status error">{error}</div>}
      {message && !error && <div className="signal-freshness-status">{message}</div>}

      <div className="signal-freshness-table-shell">
        <table className="signal-freshness-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Status</th>
              <th>Symbol</th>
              <th>TF</th>
              <th>Last Candle Close</th>
              <th>Last Seen</th>
              <th>Next Expected</th>
              <th>Age</th>
              <th>Missed</th>
              <th>Total Signals</th>
              <th>Expected</th>
              <th>Auto</th>
              <th>Active</th>
              <th>Notes</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.length === 0 && (
              <tr>
                <td colSpan="15">No freshness monitor rows found.</td>
              </tr>
            )}
            {visibleRows.map((row) => (
              <tr key={row.id}>
                <td>{row.id}</td>
                <td><span className={badgeClass(row.status)}>{row.status || '-'}</span></td>
                <td>{row.symbol || '-'}</td>
                <td>{row.timeFrame || '-'}</td>
                <td>{row.lastCandleCloseTime || '-'}</td>
                <td>{formatDate(row.lastSeenAt)}</td>
                <td>{formatDate(row.nextExpectedCandleCloseAt)}</td>
                <td>{formatDuration(row.secondsSinceLastCandleClose)}</td>
                <td>{row.consecutiveMissedCandles ?? '-'}</td>
                <td>{row.totalSignalsReceived ?? '-'}</td>
                <td>{row.expectedFlag ? 'Y' : 'N'}</td>
                <td>{row.autoDiscoveredFlag ? 'Y' : 'N'}</td>
                <td>{row.activeFlag || '-'}</td>
                <td>{row.notes || '-'}</td>
                <td className="signal-freshness-row-actions">
                  <button
                    className="signal-freshness-row-action inactive"
                    type="button"
                    disabled={loading || row.activeFlag === 'N'}
                    onClick={() => updateRow(row.id, 'inactive', { notes: 'Marked inactive from admin UI' })}
                  >
                    Inactivate
                  </button>
                  <button
                    className="signal-freshness-row-action expected"
                    type="button"
                    disabled={loading || (row.activeFlag === 'Y' && row.expectedFlag)}
                    onClick={() => updateRow(row.id, 'expected')}
                  >
                    Expect
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
